// services/authService.js
// Customer authentication and account operations using the unified Axios API client.
import api from "./api";
import { getErrorMessage } from "../utils/apiError";

function notifyAuthChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("jb_auth_change"));
  }
}

// ── token & customer state helpers ────────────────────────────────────────────

export function getAccessToken() {
  return null;
}

export function getRefreshToken() {
  return null;
}

export function clearTokens() {
  localStorage.removeItem("jb_access_token");
  localStorage.removeItem("jb_refresh_token");
  localStorage.removeItem("jb_customer");
  notifyAuthChange();
}

export function saveCustomer(customer) {
  if (customer) {
    localStorage.setItem("jb_customer", JSON.stringify(customer));
  } else {
    localStorage.removeItem("jb_customer");
  }
  notifyAuthChange();
}

export function getStoredCustomer() {
  try {
    const raw = localStorage.getItem("jb_customer");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isLoggedIn() {
  return !!getStoredCustomer();
}

// ── fetchMe cache + deduplication ────────────────────────────────────────────
let _meCache = null;
let _meInFlight = null;
const ME_TTL_MS = 30000;

export function invalidateMeCache() {
  _meCache = null;
  _meInFlight = null;
}

export async function fetchMe() {
  if (!isLoggedIn()) {
    return { success: false, error: "Not logged in" };
  }
  if (_meCache && Date.now() - _meCache.timestamp < ME_TTL_MS) {
    return _meCache.result;
  }
  if (_meInFlight) {
    return _meInFlight;
  }
  _meInFlight = api
    .get("/api/auth/me")
    .then((res) => {
      _meInFlight = null;
      const data = res.data || {};
      saveCustomer(data.customer);
      const result = {
        success: true,
        customer: data.customer,
        loyalty: data.loyalty,
      };
      _meCache = { result, timestamp: Date.now() };
      return result;
    })
    .catch((err) => {
      _meInFlight = null;
      _meCache = null;
      return { success: false, error: getErrorMessage(err, "Could not fetch profile") };
    });
  return _meInFlight;
}

// ── auth operations ───────────────────────────────────────────────────────────

export async function register(fields) {
  try {
    const res = await api.post("/api/auth/register", fields);
    return {
      success: true,
      message: res.data?.message,
      email: res.data?.email,
    };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Registration failed"),
    };
  }
}

export async function verifyRegistrationOtp(email, otp) {
  try {
    const res = await api.post("/api/auth/verify-registration-otp", { email, otp });
    return {
      success: true,
      message: res.data?.message,
      customer: res.data?.customer,
    };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Verification failed"),
    };
  }
}

export async function resendRegistrationOtp(email) {
  try {
    const res = await api.post("/api/auth/resend-registration-otp", { email });
    return {
      success: true,
      message: res.data?.message,
    };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Could not resend code"),
    };
  }
}

export async function login(email, password) {
  try {
    const res = await api.post("/api/auth/login", { email, password });
    const data = res.data || {};
    if (data.status === "otp_required") {
      return {
        success: true,
        status: "otp_required",
        email: data.email,
        message: data.message,
      };
    }
    saveCustomer(data.customer);
    invalidateMeCache();
    notifyAuthChange();
    return { success: true, customer: data.customer };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Login failed"),
    };
  }
}

export async function verifyLoginOtp(email, otp) {
  try {
    const res = await api.post("/api/auth/verify-login-otp", { email, otp });
    const data = res.data || {};
    saveCustomer(data.customer);
    invalidateMeCache();
    notifyAuthChange();
    return { success: true, customer: data.customer };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Verification failed"),
    };
  }
}

export async function resendLoginOtp(email) {
  try {
    const res = await api.post("/api/auth/resend-login-otp", { email });
    return {
      success: true,
      message: res.data?.message,
    };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Could not resend code"),
    };
  }
}

export async function logout() {
  try {
    await api.post("/api/auth/logout");
  } catch {
    // Session cleanup regardless of network status
  }
  clearTokens();
  invalidateMeCache();
  notifyAuthChange();
}

export async function forgotPassword(email) {
  try {
    const res = await api.post("/api/auth/forgot-password", { email });
    return {
      success: true,
      message: res.data?.message,
    };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Could not process request"),
    };
  }
}

export async function verifyOtp(email, otp) {
  try {
    const res = await api.post("/api/auth/verify-otp", { email, otp });
    return {
      success: true,
      message: res.data?.message,
      resetToken: res.data?.reset_token,
    };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Invalid verification code"),
    };
  }
}

export async function resetPassword(email, password, resetToken) {
  try {
    const res = await api.post("/api/auth/reset-password", {
      email,
      password,
      reset_token: resetToken,
    });
    return {
      success: true,
      message: res.data?.message,
    };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Password reset failed"),
    };
  }
}

export async function refreshAccessToken() {
  try {
    await api.post("/api/auth/refresh");
    return { success: true };
  } catch {
    clearTokens();
    return { success: false };
  }
}

export async function googleLogin(token) {
  try {
    const res = await api.post("/api/auth/google-login", { token });
    const data = res.data || {};
    if (data.success) {
      saveCustomer(data.customer);
      invalidateMeCache();
      notifyAuthChange();
    }
    return data;
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Google sign-in failed"),
    };
  }
}

// ── customer profile & booking endpoints ──────────────────────────────────────

export async function getProfile() {
  try {
    const res = await api.get("/profile");
    return res.data;
  } catch {
    return { success: false };
  }
}

export async function getBookings() {
  try {
    const res = await api.get("/my-bookings");
    const data = res.data;
    return { bookings: Array.isArray(data) ? data : data?.bookings || [] };
  } catch {
    return { bookings: [] };
  }
}

export async function updateProfile(profileData) {
  try {
    const res = await api.put("/profile", profileData);
    if (res.data?.customer) {
      saveCustomer(res.data.customer);
    }
    return res.data;
  } catch {
    return { success: false };
  }
}

export async function getInvoice(bookingId) {
  try {
    const res = await api.get(`/bookings/${bookingId}/invoice`);
    return res.data?.success ? res.data.invoice : null;
  } catch {
    return null;
  }
}

export async function getMyInvoices() {
  try {
    const res = await api.get("/customer/invoices");
    return res.data?.success ? res.data.invoices : [];
  } catch {
    return [];
  }
}